/* =============================================
   FOREST ART & FRAME - GLOBAL JS (Shopify port)

   Source: 04_Build_StaticSite/js/global.js

   Named ff-global.js, not global.js, because Dawn already ships an
   assets/global.js and theme assets are one flat namespace.

   Two behaviours differ from the static original, both deliberate:

     1. ACTIVE NAV LINK. The static version compared the last path segment
        against "index.html". Shopify has no .html anywhere, so the check is
        now a pathname comparison that understands /, /pages/x and
        /collections/x, and ignores the hash and query string.

     2. ARIA. The hamburger carries aria-expanded in the markup but the old
        script never updated it, and the mobile menu is a role="dialog" that
        could not be closed with Escape. Both are fixed here. Neither changes
        anything visual.
   ============================================= */

document.addEventListener('DOMContentLoaded', () => {
  /* --- MOBILE SUBMENU ACCORDIONS --- */
  document.querySelectorAll('[data-mobile-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const panel = btn.nextElementSibling;
      const open = btn.classList.toggle('open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.style.maxHeight = open ? panel.scrollHeight + 'px' : '0px';
    });
  });

  /* --- NAV SCROLL STATE --- */
  const nav = document.querySelector('.nav');
  if (nav) {
    const update = () => {
      nav.classList.toggle('scrolled', window.scrollY > 20);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* --- MOBILE MENU --- */
  const hamburger = document.querySelector('.nav-hamburger');
  const mobileMenu = document.querySelector('.mobile-menu');
  const mobileClose = document.querySelector('.mobile-close');

  /* Forest Art and Frame, 5 September 2026.
     Three things were wrong with the old open and close pair.

     1. `document.body.style.overflow = 'hidden'` does not lock scrolling on iOS
        Safari at all, and where it does work it throws away the scroll position,
        so closing the menu dumped you back at the top of the page. The fix is the
        standard position:fixed with a negative top offset, restoring the offset
        on close.
     2. Nothing marked the open state on the body, so CSS had no hook to hide the
        Shop page tab bar and the Pushdaddy chat bubble, both of which were
        drawing on top of the open drawer.
     3. The drawer is role="dialog" aria-modal="true" but focus was never moved
        into it, never trapped, and never returned to the burger on close, so a
        keyboard or screen reader user tabbed straight through it into the page
        behind. */
  var savedScrollY = 0;
  var lastFocused = null;

  function focusables() {
    if (!mobileMenu) return [];
    return Array.prototype.filter.call(
      mobileMenu.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
  }

  function openMenu() {
    if (!mobileMenu) return;
    lastFocused = document.activeElement;
    savedScrollY = window.scrollY || window.pageYOffset || 0;

    mobileMenu.classList.add('open');
    document.body.classList.add('ff-menu-open');
    if (hamburger) {
      hamburger.classList.add('open');
      hamburger.setAttribute('aria-expanded', 'true');
    }

    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    var first = focusables()[0];
    if (first) first.focus();
  }

  function closeMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('open');
    document.body.classList.remove('ff-menu-open');
    if (hamburger) {
      hamburger.classList.remove('open');
      hamburger.setAttribute('aria-expanded', 'false');
    }

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, savedScrollY);

    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  // Keep Tab inside the drawer while it is open.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    if (!mobileMenu || !mobileMenu.classList.contains('open')) return;
    var list = focusables();
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  if (hamburger) {
    hamburger.addEventListener('click', () => {
      if (mobileMenu && mobileMenu.classList.contains('open')) closeMenu();
      else openMenu();
    });
  }
  if (mobileClose) mobileClose.addEventListener('click', closeMenu);

  // Close menu when a nav link is clicked
  document.querySelectorAll('.mobile-nav-link').forEach((link) => {
    link.addEventListener('click', closeMenu);
  });

  // Close on outside tap
  if (mobileMenu) {
    mobileMenu.addEventListener('click', (e) => {
      if (e.target === mobileMenu) closeMenu();
    });
  }

  // Close on Escape. The overlay is a role="dialog"; Escape is expected there.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileMenu && mobileMenu.classList.contains('open')) closeMenu();
  });

  /* --- ACTIVE NAV LINK ---
     Shopify paths, not filenames. "/" only matches "/" exactly, otherwise the
     homepage link would light up on every page. Everything else matches on the
     path or on a path prefix, so /collections/mirrors marks its parent
     /collections link too. Locale prefixes such as /en-za are tolerated because
     both sides are read through the same URL parser. */
  const stripTrailing = (p) => (p.length > 1 ? p.replace(/\/+$/, '') : p);
  const here = stripTrailing(window.location.pathname);

  document.querySelectorAll('.nav-link, .mobile-nav-link, .footer-link').forEach((link) => {
    const raw = link.getAttribute('href');
    if (!raw || raw === '#' || raw.charAt(0) === '#') return;

    let target;
    try {
      target = stripTrailing(new URL(raw, window.location.origin).pathname);
    } catch (err) {
      return;
    }

    const isActive =
      target === here || (target !== '/' && here.indexOf(target + '/') === 0);

    if (isActive) link.classList.add('active');
  });

  /* --- REVEAL, an enhancement only. Forest Art and Frame, 7 October 2026 ---
     Roxy saw the home hero copy appear late and a full page capture full of
     empty bands. This used to be the only thing that ever made a .reveal block
     visible, so content waited for a deferred script and then for a scroll.

     Now ff-global.css keeps every .reveal block at full opacity from the first
     paint. Every block also gets .revealed straight away, so any page rule
     still keyed to that class shows it too. Only blocks that START BELOW the
     first screen get .ff-anim, a short upward drift as they scroll in, with
     opacity untouched. Nothing in or above the first screen ever moves, and
     with prefers-reduced-motion, or without IntersectionObserver, nothing
     moves at all. */
  const reveals = document.querySelectorAll('.reveal');
  reveals.forEach((el) => el.classList.add('revealed'));

  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reveals.length && !reduceMotion && 'IntersectionObserver' in window) {
    const firstScreen = window.innerHeight || document.documentElement.clientHeight;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('ff-anim-in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );

    reveals.forEach((el) => {
      if (el.getBoundingClientRect().top < firstScreen) return;
      el.classList.add('ff-anim');
      observer.observe(el);
    });
  }
});
